// @djsem/analysis-contract — AI Video Generation Engine (Prompt 22B).
//
// Provider-agnostic AI video generation. Parallels 22A artwork engine
// but with duration/fps/looping semantics and video-specific output types.
// Every completed job produces DJSEMAssetRecord variants via AssetManagerService.

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult, stableStringify, fnv1a, type ProviderRegistry, type DJSEMRightsLedgerService } from "@djsem/shared";
import { SeqCounter } from "@djsem/shared";
import type { DJSEMAIProviderRegistry, DJSEMPlanTier, AIResolveResult } from "@djsem/shared";
import type { DJSEMAIPromptBundle } from "./aiPromptBuilder";
import type { DJSEMAssetRecord } from "./assetManager";
import { AssetManagerService } from "./assetManager";

// ---- contracts --------------------------------------------------------------

/** The kind of video output an AI video request/job/output can produce. */
export type DJSEMAIVideoOutputType =
  | "social_teaser" | "vertical_social_clip" | "spotify_canvas_loop"
  | "youtube_visualizer_clip" | "full_track_visualizer" | "release_announcement"
  | "campaign_motion_loop" | "projection_loop" | "led_wall_loop" | "show_pack_preview";

/** Which kind of video generation provider is backing a request. */
export type DJSEMAIVideoProviderType = "disabled" | "manual_stub" | "template_renderer" | "external_video_service" | "local_video_runner";

/** Describes what a video generation provider supports (output types, formats, duration/limits). */
export interface DJSEMAIVideoProviderCapabilities {
  providerId: string;
  providerType: DJSEMAIVideoProviderType;
  supportedOutputTypes: DJSEMAIVideoOutputType[];
  supportedFormats: Array<"mp4" | "mov" | "webm">;
  supportedAspectRatios: string[];
  supportsAudioInput: boolean;
  supportsImageReference: boolean;
  supportsLooping: boolean;
  maxDurationMs?: number;
  maxVariantsPerJob: number;
  maxPromptLength?: number;
  warnings: string[];
}

/** A user/system request to generate AI video for a project/release/track/set/brand world. */
export interface DJSEMAIVideoGenerationRequest {
  id: string;
  projectId: string;
  releaseId?: string; trackId?: string; setId?: string; brandWorldId?: string;
  visualSceneIds: string[];
  sourceAssetIds: string[];
  /** Local/licensed source audio ref for providers with supportsAudioInput: true. */
  sourceAudioRef?: string;
  outputType: DJSEMAIVideoOutputType;
  promptBundleId?: string;
  userBrief?: string;
  aspectRatio: string;
  width?: number; height?: number;
  durationMs: number;
  fps?: number;
  format: "mp4" | "mov" | "webm";
  qualityMode: "draft" | "standard" | "high";
  loopBehavior?: "none" | "seamless" | "soft_loop";
  variantCount: number;
  providerId?: string;
  userConfirmedRightsWarning: boolean;
  userConfirmedCreditUsage: boolean;
  createdBy?: string;
  createdAt: string;
}

/** Lifecycle status of an AI video generation job. */
export type DJSEMAIVideoJobStatus =
  | "created" | "awaiting_rights_confirmation" | "awaiting_credits"
  | "queued" | "running" | "completed" | "failed" | "cancelled";

/** A queued/running/completed unit of work backing a video generation request. */
export interface DJSEMAIVideoGenerationJob {
  id: string;
  requestId: string;
  projectId: string;
  providerId: string;
  status: DJSEMAIVideoJobStatus;
  progress: number;
  estimatedCreditCost: number;
  chargedCreditCost?: number;
  outputAssetIds: string[];
  outputVariantIds: string[];
  promptBundleId?: string;
  safetyReportId?: string;
  warnings: string[];
  errors: string[];
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

/** A single generated video output (one variant of a job), with its rights/review status. */
export interface DJSEMAIVideoOutput {
  id: string;
  jobId: string;
  assetId: string;
  variantId: string;
  outputType: DJSEMAIVideoOutputType;
  fileRef: string;
  r2Key?: string;
  width: number; height: number;
  durationMs: number;
  fps?: number;
  format: "mp4" | "mov" | "webm";
  providerId: string;
  promptBundleId?: string;
  sourceSceneIds: string[];
  generationMetadata: Record<string, unknown>;
  reviewStatus: "not_reviewed" | "needs_review" | "approved" | "rejected";
  rightsStatus: "unknown" | "owned" | "licensed" | "restricted";
  warnings: string[];
}

/** A provider's estimate of credit cost/duration for a not-yet-run video request. */
export interface DJSEMAIVideoEstimate {
  requestId: string;
  providerId: string;
  estimatedCreditCost: number;
  estimatedDurationMs: number;
  warnings: string[];
}

/** The prompt content sent to a provider for a single video generation call. */
export interface DJSEMAIVideoPromptPayload {
  positivePrompt: string;
  negativePrompt?: string;
  brandWorldSummary?: string;
  sceneContext?: string;
  trackContext?: string;
  styleRules: string[];
  forbiddenElements: string[];
  warnings: string[];
}

// ---- provider interfaces ----------------------------------------------------

/** Normalized input passed to a video provider's estimate/generate/validateInput calls. */
export interface DJSEMAIVideoProviderInput {
  requestId: string; jobId: string;
  outputType: DJSEMAIVideoOutputType;
  prompt: DJSEMAIVideoPromptPayload;
  aspectRatio: string; width?: number; height?: number;
  durationMs: number; fps?: number;
  format: "mp4" | "mov" | "webm";
  loopBehavior?: "none" | "seamless" | "soft_loop";
  variantCount: number;
  qualityMode: "draft" | "standard" | "high";
  /** Local/licensed source audio ref — only meaningful when capabilities.supportsAudioInput. */
  sourceAudioRef?: string;
}

/** The result of a video provider's generate() call: produced variants plus warnings/errors. */
export interface DJSEMAIVideoProviderResult {
  ok: boolean;
  variants: Array<{
    fileRef: string; r2Key?: string;
    width: number; height: number;
    durationMs: number; fps?: number;
    generationMetadata: Record<string, unknown>;
  }>;
  chargedCreditCost?: number;
  warnings: string[];
  errors: string[];
}

/** Interface every AI video provider (real or stub) must implement. */
export interface DJSEMAIVideoProvider {
  id: string;
  capabilities: DJSEMAIVideoProviderCapabilities;
  estimate(input: DJSEMAIVideoProviderInput): Promise<DJSEMAIVideoEstimate>;
  generate(input: DJSEMAIVideoProviderInput): Promise<DJSEMAIVideoProviderResult>;
  validateInput(input: DJSEMAIVideoProviderInput): DJSEMValidationResult;
}

/** Criteria used to pick a video provider capable of servicing a request. */
export interface SelectAIVideoProviderInput {
  outputType: DJSEMAIVideoOutputType;
  aspectRatio?: string; format?: "mp4" | "mov" | "webm";
  preferredProviderId?: string;
}

// ---- inputs -----------------------------------------------------------------

/** Input for {@link AIVideoGenerationService.createRequest}. */
export interface CreateAIVideoRequestInput extends Omit<DJSEMAIVideoGenerationRequest, "id" | "createdAt"> {
  id?: string; createdAt?: string;
}
/** Input for {@link AIVideoGenerationService.estimateRequest}. */
export interface EstimateAIVideoInput { request: DJSEMAIVideoGenerationRequest; providerId: string; }
/** Input for {@link AIVideoGenerationService.createJob}. */
export interface CreateAIVideoJobInput { request: DJSEMAIVideoGenerationRequest; providerId: string; estimatedCreditCost: number; createdAt?: string; }
/** Input for {@link AIVideoGenerationService.queueJob}. */
export interface QueueAIVideoJobInput { job: DJSEMAIVideoGenerationJob; }
/** Input for {@link AIVideoGenerationService.completeJob}. */
export interface CompleteAIVideoJobInput { job: DJSEMAIVideoGenerationJob; providerResult: DJSEMAIVideoProviderResult; completedAt?: string; }
/** Input for {@link AIVideoGenerationService.failJob}. */
export interface FailAIVideoJobInput { job: DJSEMAIVideoGenerationJob; error: string; failedAt?: string; }
/** Input for {@link AIVideoGenerationService.attachOutputsToAssets}. */
export interface AttachAIVideoOutputsInput {
  job: DJSEMAIVideoGenerationJob; providerResult: DJSEMAIVideoProviderResult; assetManager: AssetManagerService;
  createdAt?: string;
  /** When set, each output is recorded as ai_generated_uncleared in the rights ledger (prompt 65.1). */
  rightsLedger?: DJSEMRightsLedgerService;
  assertedBy?: string;
}

/** A user's plan-level entitlements relevant to AI video generation. */
export interface AIVideoEntitlementProfile {
  planLevel: string; renderCreditsRemaining: number; watermarkRequired: boolean;
  maxDurationMs: number; maxResolution: "720p" | "1080p" | "4k";
}
/** Input for {@link AIVideoPolicyService.checkEntitlements} and {@link AIVideoPolicyService.validateDurationAndFormat}. */
export interface AIVideoPolicyInput { request: DJSEMAIVideoGenerationRequest; profile: AIVideoEntitlementProfile; }
/** Input for {@link AIVideoPolicyService.checkCredits}. */
export interface AIVideoCreditInput { request: DJSEMAIVideoGenerationRequest; profile: AIVideoEntitlementProfile; estimatedCredits: number; }
/** Input for {@link AIVideoPolicyService.estimateCredits}. */
export interface AIVideoCreditEstimateInput { request: DJSEMAIVideoGenerationRequest; }
/** Input for {@link AIVideoPolicyService.checkRightsAndSafety}. */
export interface AIVideoRightsSafetyInput { request: DJSEMAIVideoGenerationRequest; bundle?: DJSEMAIPromptBundle; }

// ---- helpers ----------------------------------------------------------------

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

const QUALITY_COST: Record<"draft" | "standard" | "high", number> = { draft: 1, standard: 3, high: 8 };
const DURATION_MULTIPLIER = (ms: number): number => Math.max(1, Math.ceil(ms / 15000)); // per 15s segment

// ---- providers --------------------------------------------------------------

/** Always-fails provider used when AI video generation is not enabled. */
export class DisabledAIVideoProvider implements DJSEMAIVideoProvider {
  id = "disabled";
  capabilities: DJSEMAIVideoProviderCapabilities = {
    providerId: "disabled", providerType: "disabled",
    supportedOutputTypes: [], supportedFormats: [], supportedAspectRatios: [],
    supportsAudioInput: false, supportsImageReference: false, supportsLooping: false,
    maxVariantsPerJob: 0, warnings: ["AI video generation is not enabled."],
  };
  async estimate(): Promise<DJSEMAIVideoEstimate> { return { requestId: "", providerId: this.id, estimatedCreditCost: 0, estimatedDurationMs: 0, warnings: this.capabilities.warnings }; }
  async generate(): Promise<DJSEMAIVideoProviderResult> { return { ok: false, variants: [], warnings: [], errors: ["AI video generation is disabled."] }; }
  validateInput(): DJSEMValidationResult { return toDJSEMValidationResult({ valid: false, issues: [{ path: "$", message: "provider disabled" }] }, "video_provider_disabled"); }
}

/** Deterministic placeholder provider for tests/offline UI — produces no real video. */
export class ManualStubAIVideoProvider implements DJSEMAIVideoProvider {
  id = "manual_stub_video";
  capabilities: DJSEMAIVideoProviderCapabilities = {
    providerId: "manual_stub_video", providerType: "manual_stub",
    supportedOutputTypes: ["social_teaser", "vertical_social_clip", "spotify_canvas_loop", "youtube_visualizer_clip", "campaign_motion_loop", "show_pack_preview"],
    supportedFormats: ["mp4", "webm"], supportedAspectRatios: ["9:16", "1:1", "16:9"],
    supportsAudioInput: false, supportsImageReference: true, supportsLooping: true,
    maxDurationMs: 120000, maxVariantsPerJob: 3, maxPromptLength: 2000,
    warnings: ["Manual stub — test/offline only. No real video generated."],
  };
  async estimate(input: DJSEMAIVideoProviderInput): Promise<DJSEMAIVideoEstimate> {
    const cost = input.variantCount * (QUALITY_COST[input.qualityMode] ?? 1) * DURATION_MULTIPLIER(input.durationMs);
    return { requestId: input.requestId, providerId: this.id, estimatedCreditCost: cost, estimatedDurationMs: 2000 * input.variantCount, warnings: [] };
  }
  async generate(input: DJSEMAIVideoProviderInput): Promise<DJSEMAIVideoProviderResult> {
    const variants = Array.from({ length: input.variantCount }, (_, i) => ({
      fileRef: `stub://video/${input.jobId}/v${i + 1}.${input.format}`,
      width: input.width ?? 1080, height: input.height ?? 1920,
      durationMs: input.durationMs, fps: input.fps ?? 30,
      generationMetadata: { stub: true, variantIndex: i },
    }));
    return { ok: true, variants, chargedCreditCost: input.variantCount * (QUALITY_COST[input.qualityMode] ?? 1) * DURATION_MULTIPLIER(input.durationMs), warnings: this.capabilities.warnings, errors: [] };
  }
  validateInput(input: DJSEMAIVideoProviderInput): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!input.prompt.positivePrompt.trim()) issues.push({ path: "$.prompt", message: "required" });
    if (input.durationMs < 1000) issues.push({ path: "$.durationMs", message: "min 1000ms" });
    if (this.capabilities.maxDurationMs && input.durationMs > this.capabilities.maxDurationMs) issues.push({ path: "$.durationMs", message: `max ${this.capabilities.maxDurationMs}ms` });
    if (input.variantCount > this.capabilities.maxVariantsPerJob) issues.push({ path: "$.variantCount", message: `max ${this.capabilities.maxVariantsPerJob}` });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "stub_video_input");
  }
}

// ---- registry ---------------------------------------------------------------

/** Registry of available AI video providers; selects the best match for a request, failing closed to "disabled". */
export class AIVideoProviderRegistry implements ProviderRegistry<DJSEMAIVideoProvider, DJSEMAIVideoProviderCapabilities, SelectAIVideoProviderInput> {
  private providers = new Map<string, DJSEMAIVideoProvider>();
  constructor() {
    this.register(new DisabledAIVideoProvider());
    this.register(new ManualStubAIVideoProvider());
  }
  register(p: DJSEMAIVideoProvider): void { this.providers.set(p.id, p); }
  getProvider(id: string): DJSEMAIVideoProvider | undefined { return this.providers.get(id); }
  listCapabilities(): DJSEMAIVideoProviderCapabilities[] { return [...this.providers.values()].map((p) => p.capabilities); }
  selectProvider(input: SelectAIVideoProviderInput): DJSEMAIVideoProvider {
    if (input.preferredProviderId) {
      const p = this.providers.get(input.preferredProviderId);
      if (p && p.capabilities.supportedOutputTypes.includes(input.outputType)) return p;
    }
    for (const p of this.providers.values()) {
      if (p.capabilities.providerType === "disabled") continue;
      if (!p.capabilities.supportedOutputTypes.includes(input.outputType)) continue;
      if (input.format && !p.capabilities.supportedFormats.includes(input.format)) continue;
      return p;
    }
    return this.providers.get("disabled")!;
  }
}

// ---- global AI model registry bridge (Prompt 66/140/141) --------------------

/** Input for {@link resolveAIVideoModel}. */
export interface ResolveAIVideoModelInput {
  plan: DJSEMPlanTier;
  preferredModelId?: string;
  requireSafePublicOutput?: boolean;
}

/**
 * Resolve an AI-video model via the global DJSEMAIProviderRegistry — plan-tier and
 * safety-aware, fails closed (ok:false) if nothing is eligible. Additive: does not replace
 * AIVideoProviderRegistry.selectProvider, which remains the fail-closed local
 * runner-selection path (disabled/manual_stub/template_renderer).
 */
export function resolveAIVideoModel(
  modelRegistry: DJSEMAIProviderRegistry,
  input: ResolveAIVideoModelInput,
): AIResolveResult {
  return modelRegistry.resolve({
    engine: "video",
    plan: input.plan,
    preferredModelId: input.preferredModelId,
    requireSafePublicOutput: input.requireSafePublicOutput,
  });
}

// ---- policy -----------------------------------------------------------------

/** Entitlement, credit, and rights/safety gating for AI video generation requests. */
export class AIVideoPolicyService {
  /**
   * Check whether a request is allowed under the user's plan entitlements (quality, duration, resolution).
   * @param input - the request and the user's entitlement profile.
   * @returns validation result describing any entitlement violations.
   */
  checkEntitlements(input: AIVideoPolicyInput): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (input.request.qualityMode === "high" && input.profile.planLevel === "free_starter")
      issues.push({ path: "$.qualityMode", message: "high-quality video requires upgraded plan" });
    if (input.request.durationMs > input.profile.maxDurationMs)
      issues.push({ path: "$.durationMs", message: `exceeds plan max ${input.profile.maxDurationMs}ms` });
    if (input.request.outputType === "full_track_visualizer" && input.profile.maxResolution === "720p")
      issues.push({ path: "$.outputType", message: "full_track_visualizer requires 1080p+ plan" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "ai_video_entitlement");
  }

  /**
   * Check whether the user has enough render credits remaining.
   * @param input - the user's profile and the estimated credit cost.
   * @returns validation result (invalid when credits are insufficient).
   */
  checkCredits(input: AIVideoCreditInput): DJSEMValidationResult {
    if (input.profile.renderCreditsRemaining >= input.estimatedCredits) return toDJSEMValidationResult({ valid: true, issues: [] }, "ai_video_credits");
    return toDJSEMValidationResult({ valid: false, issues: [{ path: "$", message: `Insufficient credits: ${input.profile.renderCreditsRemaining}/${input.estimatedCredits}` }] }, "ai_video_credits");
  }

  /**
   * Estimate the credit cost of a request based on quality, duration, and variant count.
   * @param input - the request to estimate against.
   * @returns the estimated credit cost (minimum 1).
   */
  estimateCredits(input: AIVideoCreditEstimateInput): number {
    const q = QUALITY_COST[input.request.qualityMode] ?? 1;
    const d = DURATION_MULTIPLIER(input.request.durationMs);
    return Math.max(1, q * d * input.request.variantCount);
  }

  /**
   * Check rights/safety confirmations for a request before generation is allowed.
   * @param input - the request and (optionally) its prompt bundle.
   * @returns validation result describing any missing confirmations or rights warnings.
   */
  checkRightsAndSafety(input: AIVideoRightsSafetyInput): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!input.request.userConfirmedRightsWarning) issues.push({ path: "$.userConfirmedRightsWarning", message: "user must confirm rights warning" });
    if (input.bundle?.rightsWarnings?.length) issues.push({ path: "$.bundle.rightsWarnings", message: `${input.bundle.rightsWarnings.length} rights warning(s)` });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "ai_video_rights_safety");
  }

  /**
   * Validate that a request's duration falls within the global allowed range (1s..10min).
   * @param input - the request to validate.
   * @returns validation result.
   */
  validateDurationAndFormat(input: AIVideoPolicyInput): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (input.request.durationMs < 1000) issues.push({ path: "$.durationMs", message: "min 1000ms" });
    if (input.request.durationMs > 600000) issues.push({ path: "$.durationMs", message: "max 600000ms (10 min)" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "ai_video_duration_format");
  }
}

// ---- main service -----------------------------------------------------------

/** Orchestrates AI video requests through validation, estimation, job execution, and asset attachment. */
export class AIVideoGenerationService {
  constructor(private registry: AIVideoProviderRegistry, private _policy = new AIVideoPolicyService()) {}

  /** The policy service used for entitlement/credit/rights gating. */
  get policy(): AIVideoPolicyService { return this._policy; }

  /**
   * Build a new video generation request, filling in id/createdAt defaults.
   * @param input - the request fields (id/createdAt optional).
   * @returns the constructed request.
   */
  createRequest(input: CreateAIVideoRequestInput): DJSEMAIVideoGenerationRequest {
    return { ...input, id: input.id ?? `vir_${input.projectId}_${_seq.next()}`, createdAt: input.createdAt ?? AT };
  }

  /**
   * Validate a request's required fields and confirmations.
   * @param request - the request to validate.
   * @returns validation result.
   */
  validateRequest(request: DJSEMAIVideoGenerationRequest): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!request.projectId) issues.push({ path: "$.projectId", message: "required" });
    if (!request.outputType) issues.push({ path: "$.outputType", message: "required" });
    if (request.durationMs < 1000) issues.push({ path: "$.durationMs", message: "min 1000ms" });
    if (request.variantCount < 1) issues.push({ path: "$.variantCount", message: ">= 1" });
    if (request.variantCount > 6) issues.push({ path: "$.variantCount", message: "<= 6" });
    if (!request.userConfirmedCreditUsage) issues.push({ path: "$.userConfirmedCreditUsage", message: "required" });
    if (!request.userConfirmedRightsWarning) issues.push({ path: "$.userConfirmedRightsWarning", message: "required" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "ai_video_request");
  }

  /**
   * Ask the selected provider to estimate credit cost/duration for a request.
   * @param input - the request and the provider id to estimate against.
   * @returns the provider's estimate (falls back to the disabled provider if the id is unknown).
   */
  async estimateRequest(input: EstimateAIVideoInput): Promise<DJSEMAIVideoEstimate> {
    const provider = this.registry.getProvider(input.providerId) ?? this.registry.getProvider("disabled")!;
    const pi: DJSEMAIVideoProviderInput = {
      requestId: input.request.id, jobId: `est_${input.request.id}`,
      outputType: input.request.outputType,
      prompt: { positivePrompt: input.request.userBrief ?? "", styleRules: [], forbiddenElements: [], warnings: [] },
      aspectRatio: input.request.aspectRatio, width: input.request.width, height: input.request.height,
      durationMs: input.request.durationMs, fps: input.request.fps,
      format: input.request.format, variantCount: input.request.variantCount, qualityMode: input.request.qualityMode,
      sourceAudioRef: input.request.sourceAudioRef,
    };
    return provider.estimate(pi);
  }

  /**
   * Create a job for a request, parked at the appropriate status if rights/credit confirmations are missing.
   * @param input - the request, provider id, and estimated credit cost.
   * @returns the created job.
   */
  createJob(input: CreateAIVideoJobInput): DJSEMAIVideoGenerationJob {
    const status: DJSEMAIVideoJobStatus = !input.request.userConfirmedRightsWarning
      ? "awaiting_rights_confirmation"
      : !input.request.userConfirmedCreditUsage ? "awaiting_credits" : "created";
    return {
      id: `vij_${input.request.id}_${fnv1a(stableStringify({ p: input.providerId }))}`,
      requestId: input.request.id, projectId: input.request.projectId,
      providerId: input.providerId, status, progress: 0,
      estimatedCreditCost: input.estimatedCreditCost,
      outputAssetIds: [], outputVariantIds: [],
      promptBundleId: input.request.promptBundleId,
      warnings: [], errors: [],
      createdAt: input.createdAt ?? AT,
    };
  }

  /**
   * Transition a "created" job to "queued".
   * @param input - the job to queue.
   * @returns the (possibly) updated job.
   */
  async queueJob(input: QueueAIVideoJobInput): Promise<DJSEMAIVideoGenerationJob> {
    if (input.job.status === "created") return { ...input.job, status: "queued" };
    return input.job;
  }

  /**
   * Mark a job completed or failed based on the provider result.
   * @param input - the job and the provider's generation result.
   * @returns the updated job.
   */
  completeJob(input: CompleteAIVideoJobInput): DJSEMAIVideoGenerationJob {
    if (!input.providerResult.ok || input.providerResult.variants.length === 0) {
      return { ...input.job, status: "failed", progress: 1, errors: [...input.job.errors, ...input.providerResult.errors, "No video output produced."], completedAt: input.completedAt ?? AT };
    }
    return { ...input.job, status: "completed", progress: 1, chargedCreditCost: input.providerResult.chargedCreditCost ?? input.job.estimatedCreditCost, warnings: [...input.job.warnings, ...input.providerResult.warnings], completedAt: input.completedAt ?? AT };
  }

  /**
   * Mark a job failed with an explicit error.
   * @param input - the job and the failure reason.
   * @returns the updated (failed) job.
   */
  failJob(input: FailAIVideoJobInput): DJSEMAIVideoGenerationJob {
    return { ...input.job, status: "failed", errors: [...input.job.errors, input.error], completedAt: input.failedAt ?? AT };
  }

  /**
   * Attach a job's provider output variants to the asset manager, creating a tracked asset + variant per output, and optionally recording rights-ledger entries.
   * @param input - the job, its provider result, the asset manager, and optional rights ledger/asserter.
   * @returns the created asset records (one per output variant).
   */
  async attachOutputsToAssets(input: AttachAIVideoOutputsInput): Promise<DJSEMAssetRecord[]> {
    const assets: DJSEMAssetRecord[] = [];
    for (let i = 0; i < input.providerResult.variants.length; i++) {
      const v = input.providerResult.variants[i];
      const asset = input.assetManager.createAsset({
        title: `${input.job.projectId} · video · ${input.job.id} · v${i + 1}`,
        assetType: "rendered_video", origin: "generated",
        payloadHashSource: v.fileRef, createdAt: input.createdAt ?? AT,
      });
      const withVariant = input.assetManager.addVariant({
        asset, label: `video_variant_${i + 1}`, payloadHashSource: v.fileRef,
        path: v.fileRef, r2Key: v.r2Key, format: "mp4",
      });
      input.rightsLedger?.recordAiGenerated(withVariant.id, { providerId: input.job.providerId, modelId: input.job.providerId }, input.assertedBy);
      assets.push(withVariant);
    }
    return assets;
  }
}
